import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { caseStudies, getHeroImageSrc } from '../data/caseStudies';
import ImageLightbox from '../components/ImageLightbox';
import VideoPlayer from '../components/VideoPlayer';
import PageCTA from '../components/PageCTA';
import TagList from '../components/TagList';
import MetaItem from '../components/MetaItem';
import ScrollProgressBar from '../components/ScrollProgressBar';
import BackButton from '../components/BackButton';
import HomeButton from '../components/HomeButton';
import { renderInline } from '../utils/renderInline';
import './CaseStudy.css';

const CaseStudy = () => {
  const { id } = useParams();
  const study = caseStudies.find(s => s.id === id);
  const currentIndex = caseStudies.findIndex(s => s.id === id);
  const nextStudy = caseStudies[(currentIndex + 1) % caseStudies.length];

  const [lightbox, setLightbox] = useState(null);
  const [activeSectionId, setActiveSectionId] = useState(null);
  const contentRef = useRef(null);

  useEffect(() => {
    if (!contentRef.current) return;
    const sectionEls = Array.from(contentRef.current.querySelectorAll('[id^="section-"]'));
    if (!sectionEls.length) return;

    // Switch the active ToC item when a section's top crosses 55% down the
    // viewport — early enough that the heading is comfortably visible first.
    const update = () => {
      const threshold = window.innerHeight * 0.55;
      let current = null;
      for (const el of sectionEls) {
        if (el.getBoundingClientRect().top <= threshold) current = el;
      }
      setActiveSectionId(current ? current.id : null);
    };

    window.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update, { passive: true });
    update();
    return () => {
      window.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
    };
  }, [study]);

  if (!study) {
    return (
      <div className="case-study-page">
        <div className="page-container one-col-layout">
          <div className="case-study-not-found">
            <h1>Case study not found</h1>
            <p>This project doesn't exist or may have moved.</p>
            <Link to="/" className="back-link">← Back</Link>
          </div>
        </div>
      </div>
    );
  }

  const heroImageSrc = getHeroImageSrc(study);


  const filteredSections = (study.sections || []).filter(s => s.type !== 'hero-images');

  const tocItems = filteredSections
    .map((section, index) => section.heading ? { heading: section.heading, index } : null)
    .filter(Boolean);

  // Scroll in place instead of following the hash link, which would push a
  // history entry per click and make the browser's back button step back
  // through sections instead of leaving the page.
  const scrollToSection = (e, sectionId) => {
    e.preventDefault();
    document.getElementById(sectionId)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const allImages = (study.sections || [])
    .filter(s => s.type !== 'hero-images')
    .flatMap(section => {
      if (section.type === 'image' || section.type === 'image-single') {
        const img = section.image || {};
        return [{ src: img.src || section.src || '', alt: img.alt || section.alt || '', caption: img.caption || section.caption }];
      }
      if (section.type === 'image-grid') {
        return (section.images || []).map(img => ({ src: img.src, alt: img.alt || '', caption: img.caption }));
      }
      return [];
    });

  const renderSection = (section, index) => {
    const id = section.heading ? `section-${index}` : undefined;
    const shell = (children) => (
      <div key={index} id={id} className="content-section scroll-fade">
        {children}
      </div>
    );

    switch (section.type) {
      case 'text':
        return shell(<>
          {section.label && <span className="section-label overline">{section.label}</span>}
          {section.heading && <h2>{section.heading}</h2>}
          {section.intro && <p>{renderInline(section.intro)}</p>}
          {section.table && (
            <div
              className="metric-table"
              style={{ gridTemplateColumns: section.table.columnWidths ? section.table.columnWidths.join(' ') : `repeat(${section.table.columns.length}, 1fr)` }}
            >
              {section.table.columns.map((col, i) => (
                <span key={i} className="metric-table-header-cell">{col}</span>
              ))}
              {section.table.rows.flatMap((row, i) =>
                row.map((cell, j) => (
                  <span key={`${i}-${j}`} className="metric-table-cell">{cell}</span>
                ))
              )}
            </div>
          )}
          {Array.isArray(section.numbered) && (
            <div className="numbered-list">
              {section.numbered.map((item, i) => (
                <div key={i} className="numbered-item">
                  <span className="numbered-item-number">{i + 1}</span>
                  <p>{item}</p>
                </div>
              ))}
            </div>
          )}
          {Array.isArray(section.list) && (
            <ul>{section.list.map((item, i) => <li key={i}>{item}</li>)}</ul>
          )}
          {Array.isArray(section.content)
            ? section.content.map((para, i) => <p key={i}>{renderInline(para)}</p>)
            : section.body && <p>{renderInline(section.body)}</p>
          }
        </>);

      case 'two-col-text':
        return shell(<>
          {section.label && <span className="section-label overline">{section.label}</span>}
          {section.heading && <h2>{section.heading}</h2>}
          {section.intro && <p>{renderInline(section.intro)}</p>}
          {Array.isArray(section.metrics) && (
            <div className="metrics-table">
              <div className="metrics-table-row metrics-table-header">
                <span>Metric</span>
                <span>Feature</span>
              </div>
              {section.metrics.map((row, i) => (
                <div key={i} className="metrics-table-row">
                  <span>{row.metric}</span>
                  <span>{row.feature}</span>
                </div>
              ))}
            </div>
          )}
          {Array.isArray(section.numberedItems) && (
            <ol className="numbered-items">
              {section.numberedItems.map((item, i) => (
                <li key={i}>
                  <div>
                    {Array.isArray(item.paragraphs)
                      ? item.paragraphs.map((para, j) => (
                          <p key={j}>{renderInline(para)}</p>
                        ))
                      : <p>{renderInline(item)}</p>
                    }
                  </div>
                </li>
              ))}
            </ol>
          )}
          {Array.isArray(section.list) && (
            <ul>
              {section.list.map((item, i) => (
                <li key={i}>{renderInline(item)}</li>
              ))}
            </ul>
          )}
          {Array.isArray(section.content)
            ? section.content.map((para, i) => <p key={i}>{renderInline(para)}</p>)
            : section.body && <p>{renderInline(section.body)}</p>
          }
        </>);

      case 'image':
      case 'image-single':
        return shell(<>
          {section.label && <span className="section-label overline">{section.label}</span>}
          <figure className="section-figure">
            <div
              className="section-image section-image--clickable"
              onClick={() => setLightbox({ images: allImages, index: allImages.findIndex(i => i.src === (section.image?.src || section.src)) })}
            >
              <img src={section.image?.src || section.src} alt={section.image?.alt || section.alt || ''} />
            </div>
            {(section.image?.caption || section.caption) && (
              <figcaption className="image-caption">
                {section.image?.caption || section.caption}
              </figcaption>
            )}
          </figure>
        </>);

      case 'image-grid':
        return shell(<>
          {section.label && <span className="section-label overline">{section.label}</span>}
          <div className="section-image-grid" style={section.columns ? { gridTemplateColumns: `repeat(${section.columns}, 1fr)` } : {}}>
            {section.images?.map((img, i) => (
              <figure key={i} className="section-figure">
                <div
                  className="section-image section-image--clickable"
                  onClick={() => setLightbox({ images: allImages, index: allImages.findIndex(i => i.src === img.src) })}
                >
                  <img src={img.src} alt={img.alt || ''} />
                </div>
                {img.caption && <figcaption className="image-caption">{img.caption}</figcaption>}
              </figure>
            ))}
          </div>
        </>);

      case 'metric-table':
        return shell(<>
          {section.heading && <h2>{section.heading}</h2>}
          <div
            className="metric-table"
            style={{ gridTemplateColumns: section.columnWidths ? section.columnWidths.join(' ') : `repeat(${section.columns?.length || 2}, 1fr)` }}
          >
            {section.columns?.map((col, i) => (
              <span key={i} className="metric-table-header-cell">{col}</span>
            ))}
            <div className="metric-table-divider" />
            {section.rows?.flatMap((row, i) =>
              row.map((cell, j) => (
                <span key={`${i}-${j}`} className="metric-table-cell">{cell}</span>
              ))
            )}
          </div>
          {Array.isArray(section.content) && section.content.map((para, i) => (
            <p key={i}>{renderInline(para)}</p>
          ))}
        </>);

      case 'video':
        return shell(
          <figure className="section-figure">
            <div style={section.phoneSize ? { maxWidth: '390px', margin: '0 auto' } : undefined}>
              <VideoPlayer src={section.src} loop />
            </div>
            {section.caption && <figcaption className="image-caption">{section.caption}</figcaption>}
          </figure>
        );

      case 'callout':
        return shell(
          <div className="callout-block"><p>{section.text}</p></div>
        );

      case 'stats':
        return shell(<>
          {section.label && <span className="section-label overline">{section.label}</span>}
          <div className="stats-row">
            {section.stats?.map((stat, i) => (
              <div key={i} className="stat-item">
                <span className="stat-value">{stat.value}</span>
                <span className="stat-label">{stat.label}</span>
              </div>
            ))}
          </div>
        </>);

      default:
        return null;
    }
  };

  return (
    <>
    <ScrollProgressBar />
    <BackButton />
    <HomeButton />
    <div key={id} className="case-study-page">
      <div className="page-container one-col-layout">

        {/* Hero */}
        <div className="case-study-hero">
          <h1>{study.title}</h1>
          {study.subtitle && <p className="subtitle">{study.subtitle}</p>}
          <TagList tags={study.tags} className="case-study-tags" />
          <div className="case-study-meta-row">
            <MetaItem label="Role" value={study.role} />
            <MetaItem label="Tools" value={study.tools} />
            <MetaItem label="Year" value={study.year} />
            <MetaItem label="Team" value={study.team} />
          </div>
          {heroImageSrc && (
            <div className="hero-image-full">
              <img src={heroImageSrc} alt={study.title} />
            </div>
          )}
        </div>

        {/* Table of Contents */}
        {tocItems.length > 0 && (
          <nav className="case-study-toc">
            <span className="toc-title overline">Contents</span>
            {tocItems.map((item, i) => (
              <a key={i} href={`#section-${item.index}`} className="toc-item" onClick={(e) => scrollToSection(e, `section-${item.index}`)}>
                <span className="toc-number">{String(i + 1).padStart(2, '0')}</span>
                <span className="toc-heading">{item.heading}</span>
              </a>
            ))}
          </nav>
        )}

        {/* Content Sections */}
        <div className="case-study-content" ref={contentRef}>
          {filteredSections.map((section, index) =>
            renderSection(section, index)
          )}
        </div>

        {/* Prev / Next navigation */}
        {nextStudy && (
          <PageCTA label="Next Project" title={nextStudy.title} to={`/work/${nextStudy.id}`} className="case-study-nav" />
        )}

      </div>
    </div>

    {lightbox && (
      <ImageLightbox
        images={lightbox.images}
        index={lightbox.index}
        onClose={() => setLightbox(null)}
      />
    )}

    {/* Desktop-only sticky ToC in the left margin — replaces the inline
        Contents box above 1224px. Highlights the first section until the
        reader reaches one. */}
    {tocItems.length > 0 && (
      <nav className="toc-rail" aria-label="Contents">
        <span className="toc-rail-title overline" style={{ '--i': 0 }}>Contents</span>
        {tocItems.map((item, i) => {
          const sectionId = `section-${item.index}`;
          const isActive = activeSectionId ? sectionId === activeSectionId : i === 0;
          return (
            <a
              key={sectionId}
              href={`#${sectionId}`}
              onClick={(e) => scrollToSection(e, sectionId)}
              className={`toc-rail-item${isActive ? ' toc-rail-item--active' : ''}`}
              style={{ '--i': i + 1 }}
              aria-current={isActive ? 'location' : undefined}
            >
              <span className="toc-rail-line" />
              <span className="toc-rail-label">{item.heading}</span>
            </a>
          );
        })}
      </nav>
    )}

    </>
  );
};

export default CaseStudy;
